import type {
  CoachHealthRelationshipAccessState,
  CoachHealthRelationshipLifecycle,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";
import { differenceInDays } from "date-fns";

export const GRACE_PERIOD_DAYS = 7;

export interface CoachLeaderAccessInput {
  readonly links: readonly {
    readonly leaderId: string;
    readonly createdAt: string;
    readonly lifecycle: CoachHealthRelationshipLifecycle;
  }[];
  readonly leaderLimit: number | null;
  readonly asOf: string;
}

export interface CoachLeaderAccessEntry {
  readonly leaderId: string;
  readonly joinedAt: Date;
  readonly graceDaysLeft: number;
  readonly isHardGated: boolean;
}

export interface CoachLeaderAccessSnapshot {
  readonly gatedLeaders: ReadonlyMap<string, CoachLeaderAccessEntry>;
  readonly accessByLeaderId: ReadonlyMap<
    string,
    CoachHealthRelationshipAccessState
  >;
  readonly gatedLeaderIds: ReadonlySet<string>;
  readonly softGatedCount: number;
  readonly hardGatedCount: number;
}

interface ParsedAccessTimestamp {
  readonly epochMillis: number;
  readonly epochMicros: number;
}

function parsedTime(value: string): ParsedAccessTimestamp {
  const epochMillis = Date.parse(value);
  if (!Number.isFinite(epochMillis)) throw new Error("Invalid access snapshot timestamp");
  const fraction = value.match(/\.(\d{1,6})(?:Z|[+-]\d{2}:\d{2})?$/)?.[1] ?? "";
  const microsWithinMillisecond = fraction.length > 3
    ? Number(fraction.padEnd(6, "0").slice(3, 6))
    : 0;
  const epochMicros = epochMillis * 1_000 + microsWithinMillisecond;
  if (!Number.isSafeInteger(epochMicros)) {
    throw new Error("Invalid access snapshot timestamp");
  }
  return { epochMillis, epochMicros };
}

function compareCodePoints(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function buildCoachLeaderAccessSnapshot(
  input: CoachLeaderAccessInput,
): CoachLeaderAccessSnapshot {
  const asOfTime = parsedTime(input.asOf);
  if (
    input.leaderLimit !== null &&
    (!Number.isInteger(input.leaderLimit) || input.leaderLimit < 0)
  ) {
    throw new Error("Invalid leader limit");
  }

  const seen = new Set<string>();
  const links = input.links.map((link) => {
    if (!link.leaderId || seen.has(link.leaderId)) {
      throw new Error("Invalid access relationship set");
    }
    seen.add(link.leaderId);
    const createdAtTime = parsedTime(link.createdAt);
    const accessCreatedAtTime = createdAtTime.epochMicros > asOfTime.epochMicros
      ? asOfTime
      : createdAtTime;
    return { ...link, createdAtTime, accessCreatedAtTime };
  });

  const activeLinks = links
    .filter((link) => link.lifecycle === "active")
    .sort((left, right) =>
      left.createdAtTime.epochMicros - right.createdAtTime.epochMicros ||
      compareCodePoints(left.leaderId, right.leaderId),
    );
  const gated = input.leaderLimit === null
    ? []
    : activeLinks.slice(input.leaderLimit);
  const gatedIds = new Set(gated.map((link) => link.leaderId));
  const accessByLeaderId = new Map<
    string,
    CoachHealthRelationshipAccessState
  >();
  const gatedLeaders = new Map<string, CoachLeaderAccessEntry>();
  let softGatedCount = 0;
  let hardGatedCount = 0;

  for (const link of links.sort((left, right) =>
    compareCodePoints(left.leaderId, right.leaderId),
  )) {
    if (!gatedIds.has(link.leaderId)) {
      accessByLeaderId.set(link.leaderId, "readable");
      continue;
    }

    const elapsedDays = Math.max(
      0,
      differenceInDays(
        new Date(asOfTime.epochMillis),
        new Date(link.accessCreatedAtTime.epochMillis),
      ),
    );
    const graceDaysLeft = Math.max(0, GRACE_PERIOD_DAYS - elapsedDays);
    const isHardGated = graceDaysLeft === 0;
    accessByLeaderId.set(
      link.leaderId,
      isHardGated ? "hard_gated" : "grace_period",
    );
    gatedLeaders.set(link.leaderId, {
      leaderId: link.leaderId,
      joinedAt: new Date(link.accessCreatedAtTime.epochMillis),
      graceDaysLeft,
      isHardGated,
    });
    if (isHardGated) hardGatedCount += 1;
    else softGatedCount += 1;
  }

  return {
    gatedLeaders,
    accessByLeaderId,
    gatedLeaderIds: new Set(gatedIds),
    softGatedCount,
    hardGatedCount,
  };
}
